import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import {
  createOrder, getEvent,
  type CheckoutBuyer, type CreatedOrder, type EventDetail, type TicketTier,
} from '../../lib/catalog';
import { ApiError } from '../../lib/apiClient';
import { formatDate, formatMoney } from '../../lib/format';
import { getStripe, isStripeConfigured, isStripeTestMode } from '../../lib/stripe';
import { rememberOrderToken } from '../../lib/orderTokens';
import { useAuth } from '../../auth/useAuth';
import { tierAvailability } from './eventAvailability';
import './CheckoutPage.css';

/** Engine caps: 10 per tier, 20 per order. Mirrored so the UI can't build an invalid cart. */
const MAX_PER_TIER = 10;
const MAX_PER_ORDER = 20;

type Selection = Record<string, number>;

/**
 * Payment step.
 *
 * By the time this renders the order already exists on the engine as a pending
 * reservation with a server-computed total; the card only confirms the
 * PaymentIntent. Card details go straight from the Stripe iframe to Stripe —
 * they never enter this component, this bundle, or our servers.
 */
function PaymentStep({
  order,
  event,
  onBack,
}: {
  order: CreatedOrder;
  event: EventDetail;
  onBack: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    // This form only ever mounts for an order that requires payment, so the secret is
    // present. Narrowed explicitly because the type is now optional: a $0 order returns
    // no clientSecret at all and never reaches this component.
    if (!order.clientSecret) {
      setError('This order does not require payment.');
      setSubmitting(false);
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message ?? 'Please check your card details.');
      setSubmitting(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      clientSecret: order.clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/orders/${order.orderId}`,
      },
      // Stay on the page for cards; only redirect-based methods leave.
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message ?? 'That payment could not be completed.');
      setSubmitting(false);
      return;
    }

    // Payment is authorized. Tickets are NOT issued yet — the engine only
    // activates them when Stripe's signed webhook arrives, so the confirmation
    // screen polls rather than assuming success.
    navigate(`/orders/${order.orderId}`, { replace: true });
  }

  return (
    <form className="checkout__payment" onSubmit={handleSubmit}>
      <h2 className="checkout__step-title">Payment</h2>

      {isStripeTestMode && (
        <p className="checkout__test-note">
          Test mode, use card <span className="mono">4242 4242 4242 4242</span>, any future
          expiry, any CVC.
        </p>
      )}

      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <p className="checkout__error" role="alert">
          {error}
        </p>
      )}

      <div className="checkout__actions">
        <button
          type="button"
          className="button button--ghost"
          onClick={onBack}
          disabled={submitting}
        >
          Back
        </button>
        <button
          type="submit"
          className="button button--primary"
          disabled={!stripe || submitting}
        >
          {submitting ? 'Processing…' : `Pay ${formatMoney(order.totalCents)}`}
        </button>
      </div>

      <p className="checkout__secure">
        Card details are entered directly with Stripe. Voleto never sees or stores them.
      </p>
      <p className="checkout__event-note">
        {event.title} · {formatDate(event.date)}
      </p>
    </form>
  );
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { user, isAuthenticated } = useAuth();

  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [selection, setSelection] = useState<Selection>({});
  const [buyer, setBuyer] = useState<CheckoutBuyer>({ firstName: '', lastName: '', email: '' });
  const [order, setOrder] = useState<CreatedOrder | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;

    getEvent(eventId)
      .then((result) => {
        if (!cancelled) setEvent(result);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load this event.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // Prefill for a signed-in buyer; guests type their own details.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    setBuyer((current) =>
      current.email === ''
        ? {
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            email: user.email,
          }
        : current,
    );
  }, [isAuthenticated, user]);

  const buyableTiers = useMemo(
    () => (event?.tiers ?? []).filter((tier) => tierAvailability(tier).buyable),
    [event],
  );

  const items = useMemo(
    () =>
      Object.entries(selection)
        .filter(([, quantity]) => quantity > 0)
        .map(([tierId, quantity]) => ({ tierId, quantity })),
    [selection],
  );

  const totalTickets = items.reduce((sum, item) => sum + item.quantity, 0);

  /**
   * Preview only. The engine recomputes the price from the database and that
   * figure is what gets charged — this exists so the buyer can see a running
   * total, never to determine one.
   */
  const previewTotalCents = useMemo(() => {
    if (!event) return 0;
    return items.reduce((sum, item) => {
      const tier = event.tiers.find((t) => t.id === item.tierId);
      return sum + (tier ? tier.priceCents * item.quantity : 0);
    }, 0);
  }, [event, items]);

  function setQuantity(tier: TicketTier, quantity: number) {
    const cap = Math.min(MAX_PER_TIER, tier.remaining);
    const next = Math.max(0, Math.min(quantity, cap));
    setSelection((current) => ({ ...current, [tier.id]: next }));
  }

  async function handleReserve(e: React.FormEvent) {
    e.preventDefault();
    if (!eventId || items.length === 0) return;

    if (totalTickets > MAX_PER_ORDER) {
      setError(`Maximum ${MAX_PER_ORDER} tickets per order.`);
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const created = await createOrder({ eventId, buyer, items });
      // Guests have no account, so this token is the ONLY way back to the order.
      // Kept in sessionStorage, never in the URL.
      rememberOrderToken(created.orderId, created.accessToken);

      // A ZERO-TOTAL ORDER IS ALREADY FULFILLED. There is no PaymentIntent and therefore
      // no clientSecret, so mounting <Elements> would hand Stripe an undefined secret and
      // leave the buyer on a payment form for an order that is already complete. Go
      // straight to the confirmation page, which reads the order with the token just
      // remembered and already renders a complete order with its QR codes.
      if (!created.paymentRequired) {
        navigate(`/orders/${created.orderId}`, { replace: true });
        return;
      }

      setOrder(created);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(
          err.status === 429
            ? 'Too many payment attempts. Please wait a few minutes and try again.'
            : err.message,
        );
      } else {
        setError('Could not start checkout. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  }

  if (loading) return <p className="checkout__status">Loading checkout…</p>;
  if (loadError || !event) {
    return (
      <div className="checkout__status checkout__status--error">
        <p role="alert">{loadError ?? 'Event not found.'}</p>
        <Link to="/events" className="button button--secondary">
          Browse events
        </Link>
      </div>
    );
  }

  const stripePromise = getStripe();

  return (
    <div className="checkout">
      <nav className="checkout__breadcrumb">
        <Link to={`/events/${event.id}`}>← Back to {event.title}</Link>
      </nav>

      <div className="checkout__layout">
        <div className="checkout__main">
          {!order ? (
            <form id="checkout-details" className="checkout__form" onSubmit={handleReserve}>
              <section className="checkout__section">
                <h2 className="checkout__step-title">1. Choose tickets</h2>

                {buyableTiers.length === 0 ? (
                  <p className="checkout__empty">
                    There are no tickets available for this event right now.
                  </p>
                ) : (
                  <ul className="qty-list">
                    {buyableTiers.map((tier) => {
                      const availability = tierAvailability(tier);
                      const quantity = selection[tier.id] ?? 0;
                      const cap = Math.min(MAX_PER_TIER, tier.remaining);
                      return (
                        <li key={tier.id} className="qty">
                          <div className="qty__info">
                            <span className="qty__name">{tier.name}</span>
                            {tier.description && (
                              <span className="qty__description">{tier.description}</span>
                            )}
                            <span className={`qty__availability qty__availability--${availability.tone}`}>
                              {availability.label}
                            </span>
                          </div>

                          <span className="qty__price mono">{formatMoney(tier.priceCents)}</span>

                          <div className="qty__stepper">
                            <button
                              type="button"
                              className="qty__button"
                              onClick={() => setQuantity(tier, quantity - 1)}
                              disabled={quantity === 0}
                              aria-label={`Remove one ${tier.name} ticket`}
                            >
                              −
                            </button>
                            <span className="qty__value mono" aria-live="polite">
                              {quantity}
                            </span>
                            <button
                              type="button"
                              className="qty__button"
                              onClick={() => setQuantity(tier, quantity + 1)}
                              disabled={quantity >= cap}
                              aria-label={`Add one ${tier.name} ticket`}
                            >
                              +
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section className="checkout__section">
                <h2 className="checkout__step-title">2. Your details</h2>
                {!isAuthenticated && (
                  <p className="checkout__guest-note">
                    No account needed, we&apos;ll email your tickets. Already have one?{' '}
                    <Link to="/login">Sign in</Link>.
                  </p>
                )}

                <div className="checkout__fields">
                  <label className="field">
                    <span className="field__label">First name</span>
                    <input
                      className="field__input"
                      type="text"
                      required
                      autoComplete="given-name"
                      value={buyer.firstName}
                      onChange={(e) => setBuyer((b) => ({ ...b, firstName: e.target.value }))}
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Last name</span>
                    <input
                      className="field__input"
                      type="text"
                      required
                      autoComplete="family-name"
                      value={buyer.lastName}
                      onChange={(e) => setBuyer((b) => ({ ...b, lastName: e.target.value }))}
                    />
                  </label>
                  <label className="field checkout__field--wide">
                    <span className="field__label">Email</span>
                    <input
                      className="field__input"
                      type="email"
                      required
                      autoComplete="email"
                      value={buyer.email}
                      onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))}
                    />
                    <span className="field__hint">Your tickets and QR codes go here.</span>
                  </label>
                </div>
              </section>

              {error && (
                <p className="checkout__error" role="alert">
                  {error}
                </p>
              )}

              {!isStripeConfigured && (
                <p className="checkout__error" role="alert">
                  Payments are not configured for this environment.
                </p>
              )}

            </form>
          ) : stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: order.clientSecret,
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#F0B429',
                    colorBackground: '#0F1420',
                    colorText: '#F7FAFC',
                    colorDanger: '#EF4444',
                    fontFamily: 'Inter Variable, Inter, system-ui, sans-serif',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              <PaymentStep order={order} event={event} onBack={() => setOrder(null)} />
            </Elements>
          ) : (
            <p className="checkout__error" role="alert">
              Payments are unavailable right now.
            </p>
          )}
        </div>

        <aside className="checkout__summary">
          <h2 className="checkout__summary-title">Order summary</h2>

          <ul className="summary__lines">
            {items.length === 0 && <li className="summary__empty">No tickets selected yet.</li>}
            {items.map((item) => {
              const tier = event.tiers.find((t) => t.id === item.tierId);
              if (!tier) return null;
              return (
                <li key={item.tierId} className="summary__line">
                  <span className="summary__line-name">
                    {tier.name} <span className="mono">×{item.quantity}</span>
                  </span>
                  <span className="mono">{formatMoney(tier.priceCents * item.quantity)}</span>
                </li>
              );
            })}
          </ul>

          <dl className="summary__totals">
            <div className="summary__row summary__row--total">
              <dt>Total</dt>
              <dd className="mono">{formatMoney(order?.totalCents ?? previewTotalCents)}</dd>
            </div>
          </dl>

          {!order && (
            <div className="summary__action">
              {/* Directly under the figure it qualifies, and above the commitment. The
                  refund terms are the part a buyer only cares about later, which is
                  exactly why they belong before the button rather than after it. */}
              <p className="checkout__inclusive">
                (Ticket prices include all applicable platform and credit card processing
                fees, which are non-refundable. Learn more in our{' '}
                <Link to="/terms#fees">Terms and Conditions</Link>.)
              </p>

              {/* Outside the form, submitting it by id. The fields belong in the left
                  column and the action belongs under the total; the HTML form attribute
                  is what lets both be true without duplicating state. */}
              <button
                type="submit"
                form="checkout-details"
                className="button button--primary button--block"
                disabled={items.length === 0 || creating || !isStripeConfigured}
              >
                {creating ? 'Reserving…' : 'Continue to payment'}
              </button>
            </div>
          )}

          {order && (
            <p className="summary__server-note">
              Total confirmed by Voleto&apos;s server, <span className="mono">{formatMoney(order.totalCents)}</span>.
            </p>
          )}

        </aside>
      </div>
    </div>
  );
}
